// CmdInterpreter.js
/**
 * @fileoverview CmdInterpreter validates and interprets commands returned by agent API.
 */
import { UserActions } from 'BMapsCmds';
import { BackgroundColors } from 'BMapsSrc/themes';
import { App } from 'BMapsSrc/BMapsApp';
import { parseResidueSpec } from 'BMapsSrc/util/mol_info_utils';
import { AtomGroup } from '../model/atomgroups';

// Validation information
const knownCommands = {
    SetView: {
        argInfo: [{
            options: ['protein', 'ligand', 'publication'],
        }],
    },
    ResetView: {
        argInfo: [],
    },
    SetDisplayStyle: {
        argInfo: [{
            options: ['default', 'wireframe', 'sticks', 'ballandstick', 'spacefill', 'cartoon', 'surface'],
        }],
    },
    SetHotspots: {
        argInfo: [{
            type: 'boolean',
        }],
    },
    SetColorScheme: {
        argInfo: [{
            options: ['default', ...Object.keys(BackgroundColors)],
        }],
    },
    Select: {
        argInfo: [{
            type: 'string',
            // Ideally this should validate the select argument semantics:
            // either a selection keyword or an existing resSpec or compound name
        }],
    },
    OpenDock: {
        argInfo: [],
    },
    RequestMinimize: {
        argInfo: [],
    },
    ActivateNextCompound: {
        argInfo: [{
            type: 'number',
        }],
    },
    SetCustomAtomGroupStyle: {
        argInfo: [{ type: 'string' }, { type: 'object' }],
        execute: async (resSpec, styleObj) => {
            const atomGroups = findAtomGroups({ resSpec });
            if (atomGroups.length === 0) {
                console.log(`No atom groups matching ${resSpec}`);
                return;
            }
            atomGroups.forEach((group) => UserActions.SetCustomAtomGroupStyle(group, styleObj));
            UserActions.RedrawScene();
        },
    },
};

/**
 * Find atom groups meeting the provided residue specification.
 */
function findAtomGroups({
    resSpec, resn, resi, chain, insCode, altLoc, modelNum,
}) {
    if (resSpec) {
        const parsedSpec = parseResidueSpec(resSpec);
        if (parsedSpec) {
            return findAtomGroups(parsedSpec);
        } else {
            console.warn(`Could not parse residue spec: "${resSpec}"`);
            return [];
        }
    }

    // Here we should have resn, resi, chain, insCode, altLoc, modelNum
    // At least one of the spec components must be provided.
    const emptySpec = !resn && resi == null && !chain && !insCode && !altLoc && modelNum == null;
    if (emptySpec) {
        return [];
    }

    let found = App.Workspace.allCaseData().flatMap((cd) => cd.allAtomGroups()).filter((ag) => (
        (!resn || ag.resn === resn)
        && (resi == null || ag.resi === resi)
        && (!chain || ag.chain === chain)
        && (!insCode || ag.insCode === insCode)
        && (!altLoc || ag.altLoc === altLoc)
        && (modelNum == null || ag.modelNum === modelNum)
    ));

    // The resSpec of polymers (chains) match the first residue, so:
    //   1. If only chain is requested, and there is a polymer in the results, return only polymer.
    //   2. If not only chain, exclude polymers from the results.
    const onlyChain = chain && !resn && resi == null && !insCode && !altLoc; // exclude modelNum
    const anyPolymer = found.some((ag) => AtomGroup.PolymerTypes.includes(ag.type));
    found = found.filter((ag) => (
        (onlyChain && (!anyPolymer || AtomGroup.PolymerTypes.includes(ag.type)))
        || (!onlyChain && !AtomGroup.PolymerTypes.includes(ag.type))
    ));

    return found;
}

function getCommandLabel(action, args) {
    switch (action) {
        case 'SetView': return `Set the view to ${args[0]}`;
        case 'ResetView': return 'Reset view';
        case 'SetDisplayStyle': return `Set display style to ${args[0]}`;
        case 'SetHotspots': return `Set hotspots ${args[0] ? 'On' : 'Off'}`;
        case 'SetColorScheme': return `Set background color ${args[0]}`;
        case 'Select': return `Select ${args[0]}`;
        case 'OpenDock': return 'Open the docking panel';
        case 'RequestMinimize': return 'Minimize compound';
        case 'ActivateNextCompound': {
            const step = args[0];
            return `Step focus compound ${step < 0 ? step : `+${step}`}`;
        }
        case 'SetCustomAtomGroupStyle': {
            const [spec, style] = args;
            const parts = Object.entries(style).map(([key, value]) => {
                switch (key) {
                    case 'molStyle': return `molecular style ${value}`;
                    case 'color': return `color ${value}`;
                    case 'colorAllAtoms': return `color all atoms ${value ? 'on' : 'off'}`;
                    case 'showLabel': return `label ${value ? 'on' : 'off'}`;
                    default: return '';
                }
            }).filter(Boolean);
            return `Style ${spec}: ${parts.join(', ')}`;
        }
        default: return action;
    }
}

export default class CmdInterpreter {
    /**
     * Apply a command array returned by agent API.
     * This validates and executes each command representation.
     * @param {Array<[string, ...]>} cmdArray
     */
    static async interpretCommand(cmdArray) {
        if (!Array.isArray(cmdArray) || cmdArray.length === 0) {
            console.log('Could not interpret command from words');
            return;
        }
        console.log(`Applying interpreted command from words: ${JSON.stringify(cmdArray)}`);

        for (const unit of cmdArray) {
            const { action, args, error } = CmdInterpreter.validateCommand(unit);
            if (error) {
                console.log(`Validation error: ${error}`);
                continue;
            }
            const cmdDef = knownCommands[action];
            if (cmdDef.execute) {
                await cmdDef.execute(...args);
            } else {
                await UserActions[action](...args);
            }
        }
    }

    /**
     * Validates a command action and arguments, returning them if valid.
     * @param {[string, ...]} cmdUnit
     * @returns {{ action: string, args: [] } | { error: string }}
     */
    static validateCommand(cmdUnit) {
        if (!Array.isArray(cmdUnit) || cmdUnit.length === 0) {
            return { error: 'Invalid command' };
        }
        const [action, ...args] = cmdUnit;
        const { argInfo } = knownCommands[action] || {};
        if (!argInfo) {
            return { error: `Invalid action ${action}` };
        }

        if (args.length !== argInfo.length) {
            return { error: `Invalid number of arguments for ${action}: ${args.length} provided, ${argInfo.length} expected` };
        }

        for (let i = 0; i < args.length; i++) {
            const error = CmdInterpreter.validateArg(args[i], argInfo[i]);
            if (error) return { error: `Invalid argument ${i} for ${action}: ${error}` };
        }
        return { action, args };
    }

    /**
     * Validate a single command argument
     * @param {any} arg
     * @param {{ type: string?, options: []?}} argInfo
     * @returns {string?} error string or null if successful
     */
    static validateArg(arg, argInfo) {
        const { type, options } = argInfo;
        if (type) {
            const provided = typeof arg;
            switch (type) {
                case 'string':
                    // Note: eslint enforces always comparing typeof results to string literals
                    if (provided !== 'string') return `${type} required, ${provided} provided`;
                    break;
                case 'boolean':
                    if (provided !== 'boolean') return `${type} required, ${provided} provided`;
                    break;
                case 'object':
                    if (provided !== 'object' || arg == null) return `${type} required, ${provided} provided`;
                    break;
                case 'number':
                    if (provided !== 'number') return `${type} required, ${provided} provided`;
                    break;
                // no default
            }
        }
        if (options) {
            if (!options.includes(arg)) return 'invalid option';
        }
        return null;
    }

    static printCommandInfo(cmdUnit) {
        const { action, args, error } = CmdInterpreter.validateCommand(cmdUnit);
        if (error) return null;
        return getCommandLabel(action, args);
    }
}
